import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getLocalDateString } from "../../../../utils";

const getCurrentDate = () => new Date().toISOString().split("T")[0];
const getCurrentTime = () =>
  new Date().toTimeString().split(":").slice(0, 2).join(":");

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const AddPrescription = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const today = getLocalDateString();

  const [extraMedicines, setExtraMedicines] = useState([]);

  // Ticket-based selection (patient comes from ticket)
  const [ticketSearchTerm, setTicketSearchTerm] = useState("");
  const [ticketInputValue, setTicketInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);
  const [selectedTicketSnapshot, setSelectedTicketSnapshot] = useState(null);

  const [input, setInput] = useState({
    date: getCurrentDate(),
    time: getCurrentTime(),
    doctor: auth?.user?.user?.id || "",
    patient: "",
    ticket: "",
    duration: "",
    total_price: 0,
    items: [],
  });

  // When a ticket is already selected, fetch with no search so the selected ticket stays in the options list
  const effectiveSearch = input.ticket ? "" : ticketSearchTerm;

  const {
    data: ticketsData,
    isLoading: isLoadingTickets,
  } = useFetchObjects(
    ["tickets-doctor", today, effectiveSearch, forceUpdate],
    `tickets/?status=1&visit_date=${today}&search=${encodeURIComponent(effectiveSearch)}&page_size=200`,
    token
  );

  const tickets = ticketsData?.results || ticketsData || [];
  const ticketsList = Array.isArray(tickets) ? tickets : [];

  const { data: medicines, isLoading: isLoadingMedicines } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/`,
    token
  );

  useEffect(() => {
    setInput({ ...input, extra_medicines: extraMedicines });
  }, [extraMedicines]);

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const autocompleteRef = useRef(null);
  const dosageRefs = useRef([]);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);
  const notationRefs = useRef([]);
  const isBeforeRefs = useRef([]);
  const unitsPerMealRefs = useRef([]);

  const {
    handleAdd: handleAddPrescription,
    resetForm,
    loading,
  } = useAdd(
    "prescriptions",
    token,
    t("pharmacy.prescriptionCreatedSuccess"),
    t("pharmacy.prescriptionCreateFail")
  );

  useEffect(() => {
    if (resetForm) {
      setInput({
        date: getCurrentDate(),
        time: getCurrentTime(),
        doctor: auth?.user?.user?.id || "",
        patient: "",
        ticket: "",
        duration: "",
        total_price: 0,
        items: [],
      });
      setFormErrors({});
      setTicketInputValue("");
      setSelectedTicketSnapshot(null);
    }
  }, [resetForm]);

  const handleChangePrescriptionField = (e) => {
    const { name, value } = e.target;
    setInput({ ...input, [name]: value });
  };

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      setInput((prevInput) => {
        const newItems = [
          ...prevInput.items,
          {
            stock_medicine: medicine.id,
            name: medicine.medicine_display,
            quantity: 1,
            dosage: 1,
            sell_price: medicine.sell_price,
            total_price: medicine.sell_price,
            available_stock: medicine.quantity,
            notation: "",
            is_before: "after",
            units_per_meal: 1,
            times_per_24: 1,
          },
        ];
        return {
          ...prevInput,
          items: newItems,
          total_price: calculateTotalPrice(newItems),
        };
      });

      setAutocompleteInputValue("");
      if (autocompleteRef.current) {
        autocompleteRef.current.focus();
      }
    }
  };

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];
    items[index][name] = value ? parseInt(value) || value : "";
    if (name === "quantity" || name === "sell_price") {
      items[index].total_price =
        items[index].sell_price * items[index].quantity;
    }
    setInput({ ...input, items, total_price: calculateTotalPrice(items) });
  };

  const handleKeyDown = (e, index, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (field === "dosage" && unitsPerMealRefs.current[index]) {
        unitsPerMealRefs.current[index].focus();
      } else if (field === "units_per_meal" && quantityRefs.current[index]) {
        quantityRefs.current[index].focus();
      } else if (field === "quantity" && notationRefs.current[index]) {
        notationRefs.current[index].focus();
      } else if (field === "notation" && dosageRefs.current[index + 1]) {
        dosageRefs.current[index + 1].focus();
      }
    }
  };

  const addItems = () => {
    setExtraMedicines([
      ...extraMedicines,
      {
        title: "",
        dosage: "",
        quantity: "",
        notation: "",
        units_per_meal: "",
        times_per_24: "",
        is_before: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setExtraMedicines(extraMedicines?.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...extraMedicines];
    newItems[index][field] = value;
    setExtraMedicines(newItems);
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    setInput({ ...input, items, total_price: calculateTotalPrice(items) });
  };

  const calculateTotalPrice = (items) =>
    items.reduce((sum, item) => sum + (item.total_price || 0), 0);

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (!input.date) {
      errors.date = t("pharmacy.dateRequired");
      isValid = false;
    }
    if (!input.doctor) {
      errors.doctor = t("pharmacy.doctorRequired");
      isValid = false;
    }
    if (!input.ticket) {
      errors.ticket = t("doctor.selectTicketRequired", "Select a visit ticket.");
      isValid = false;
    }
    if (!input.patient && !input.ticket) {
      errors.patient = t("pharmacy.patientRequired");
      isValid = false;
    }

    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }

      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }

      if (!item.sell_price) {
        errors[`sell_price_${index}`] = t("pharmacy.priceRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);

    if (isValid) {
      const payload = {
        ...input,
        ticket: input.ticket || null,
        extra_medicines: extraMedicines,
      };
      handleAddPrescription(payload);
      setInput((prevInput) => ({
        ...prevInput,
        patient: "",
        ticket: "",
      }));
      setTicketInputValue("");
    }
  };

  const handleTicketInputChange = (event, newInputValue) => {
    setTicketInputValue(newInputValue);
    debouncedSetTicketSearch(newInputValue);
  };

  const getTicketOptionLabel = (option) =>
    `${option.ticket_number || option.id} — ${option.patient_name_display || ""} ${option.patient_phone_display ? `(${option.patient_phone_display})` : ""}`;

  const handleTicketChange = (event, newValue) => {
    if (newValue) {
      setInput({
        ...input,
        ticket: newValue.id,
        patient: newValue.patient || "",
      });
      setTicketInputValue(getTicketOptionLabel(newValue));
      setTicketSearchTerm("");
      setSelectedTicketSnapshot(newValue);
    } else {
      setInput({ ...input, ticket: "", patient: "" });
      setTicketInputValue("");
      setTicketSearchTerm("");
      setSelectedTicketSnapshot(null);
    }
  };

  const debouncedSetTicketSearch = useCallback(
    debounce((value) => setTicketSearchTerm(value), 300),
    []
  );

  const selectedTicketFromList = ticketsList.find((t) => t.id === input.ticket);
  const selectedTicket =
    selectedTicketFromList ||
    (input.ticket && selectedTicketSnapshot?.id === input.ticket ? selectedTicketSnapshot : null);

  const ticketOptions =
    selectedTicket &&
    !ticketsList.some((t) => t.id === selectedTicket.id)
      ? [selectedTicket, ...ticketsList]
      : ticketsList;

  return (
    <Card
      sx={{
        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
        borderRadius: 4,
        backgroundColor: "#f4f6f8",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {/* Go Back Icon and Title */}
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("pharmacy.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("pharmacy.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon size="large" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
          textAlign="center"
          gutterBottom
        >
          {t("pharmacy.addPrescriptionTitle")}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("pharmacy.patientInfo")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.prescriptionDate")}
                name="date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={input.date}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.date}
                helperText={formErrors.date}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.time")}
                name="time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={input.time}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.time}
                helperText={formErrors.time}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={ticketOptions}
                getOptionLabel={getTicketOptionLabel}
                inputValue={ticketInputValue}
                onInputChange={handleTicketInputChange}
                value={selectedTicket}
                onChange={handleTicketChange}
                loading={isLoadingTickets}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography>
                      <strong>{option.ticket_number}</strong>
                      {" — "}
                      {option.patient_name_display || ""}
                      {option.patient_phone_display ? ` (${option.patient_phone_display})` : ""}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("doctor.visitTicket", "Visit ticket")}
                    placeholder={t("doctor.selectTicketPlaceholder", "Search by ticket number or patient name")}
                    variant="outlined"
                    error={!!formErrors.ticket}
                    helperText={formErrors.ticket}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingTickets ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                )}
              />
            </Grid>
            {selectedTicket && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("pharmacy.patient")}
                  value={selectedTicket.patient_name_display || ""}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.duration")}
                name="duration"
                variant="outlined"
                value={input.duration}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.duration}
                helperText={formErrors.duration}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Search Medicine Section */}
        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("pharmacy.addMedicine")}
          </Typography>
          <Autocomplete
            ref={autocompleteRef}
            value={null}
            inputValue={autocompleteInputValue}
            onInputChange={(event, newInputValue) => {
              setAutocompleteInputValue(newInputValue);
            }}
            onChange={(event, value) => {
              handleAddNewItem(value);
              setAutocompleteInputValue("");
            }}
            options={
              medicines
                ? medicines.filter((medicine) =>
                    medicine.medicine_display
                      .toLowerCase()
                      .startsWith(autocompleteInputValue.toLowerCase())
                  )
                : []
            }
            getOptionLabel={(option) =>
              `${option.medicine_display} (${option.category_display})`
            }
            loading={isLoadingMedicines}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  padding: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "#eef2f5",
                  },
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap", color: "text.primary" }}
                >
                  {option.medicine_display}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.category")}:</strong>{" "}
                  {option.category_display}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.price")}:</strong> {option.sell_price}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.expiryDate")}:</strong>{" "}
                  {option.expiry_date}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pharmacy.searchMedicine")}
                variant="outlined"
                placeholder={t("pharmacy.searchMedicinePlaceholder")}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingMedicines ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                }}
              />
            )}
          />
        </Box>

        {/* Prescription Items Table */}
        <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#eef2f5" }}>
              <TableRow>
                <TableCell>{t("pharmacy.medicineName")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.availableStock")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.dosage")}</TableCell>
                <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                  {t("pharmacy.unitsPerMeal")}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                  {t("pharmacy.timesPer24")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
                <TableCell align="center">{t("pharmacy.price")}</TableCell>
                <TableCell align="center">{t("pharmacy.notation")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.isBeforeMeal")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
                <TableCell align="center">{t("pharmacy.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {input.items.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="center">{item.available_stock}</TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (dosageRefs.current[index] = el)}
                      name="dosage"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.dosage}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "dosage")}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (unitsPerMealRefs.current[index] = el)}
                      name="units_per_meal"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.units_per_meal}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) =>
                        handleKeyDown(e, index, "units_per_meal")
                      }
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      name="times_per_24"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.times_per_24 ?? ""}
                      onChange={(e) => handleChangeItemField(index, e)}
                      inputProps={{ min: 1, max: 24 }}
                      placeholder="1"
                      sx={{ backgroundColor: "#fff", borderRadius: 2, width: 70 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (quantityRefs.current[index] = el)}
                      name="quantity"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.quantity}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "quantity")}
                      error={!!formErrors[`quantity_${index}`]}
                      helperText={formErrors[`quantity_${index}`]}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (priceRefs.current[index] = el)}
                      name="sell_price"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.sell_price}
                      onChange={(e) => handleChangeItemField(index, e)}
                      disabled
                      sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (notationRefs.current[index] = el)}
                      name="notation"
                      type="text"
                      size="small"
                      variant="outlined"
                      value={item.notation}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "notation")}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControl fullWidth size="small">
                      <Select
                        value={item.is_before}
                        onChange={(e) => handleChangeItemField(index, e)}
                        name="is_before"
                        ref={(el) => (isBeforeRefs.current[index] = el)}
                      >
                        <MenuItem value="before">
                          {t("pharmacy.before")}
                        </MenuItem>
                        <MenuItem value="med">{t("pharmacy.med")}</MenuItem>
                        <MenuItem value="after">{t("pharmacy.after")}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={600}>{item.total_price}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("pharmacy.removeItem")} arrow>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container>
          {extraMedicines?.map((items, index) => {
            return (
              <Grid container justifyContent={"space-between"} gap={2} mt={2}>
                <TextField
                  name="title"
                  label={t("pharmacy.medicineName")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.title}
                  onChange={(e) =>
                    handleItemChange(index, "title", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="quantity"
                  label={t("pharmacy.quantity")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
                <TextField
                  name="dosage"
                  label={t("pharmacy.dosage")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.dosage}
                  onChange={(e) =>
                    handleItemChange(index, "dosage", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
                <TextField
                  name="notation"
                  label={t("pharmacy.notation")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.notation}
                  onChange={(e) =>
                    handleItemChange(index, "notation", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="units_per_meal"
                  label={t("pharmacy.unitsPerMeal")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.units_per_meal}
                  onChange={(e) =>
                    handleItemChange(index, "units_per_meal", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="times_per_24"
                  label={t("pharmacy.timesPer24")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.times_per_24 ?? ""}
                  onChange={(e) =>
                    handleItemChange(index, "times_per_24", e.target.value)
                  }
                  placeholder="1"
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="is_before"
                  label={t("pharmacy.isBeforeMeal")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.is_before}
                  onChange={(e) =>
                    handleItemChange(index, "is_before", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <IconButton onClick={() => removeItem(index)} color="error">
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Grid>
            );
          })}
        </Grid>

        <Button variant="outlined" onClick={addItems} sx={{ mt: 1 }}>
          Add Extra Medicines
        </Button>

        {/* Save Button and Total Price Display */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <TextField
            size="small"
            label={t("pharmacy.totalPrice")}
            type="number"
            value={input.total_price}
            sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
            disabled
          />
          <Button
            size="small"
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              fontWeight: 700,
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? t("pharmacy.saving") : t("pharmacy.savePrescription")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AddPrescription;
